import { useMemo } from "react";
import moment from "moment-timezone";
import { usePlatform } from "../PlatformContext";
import { Circle } from "../types/platform.types";
import { useUserTimezoneOption } from "./useUserTimezoneOption";
import { getUserMeetingRoleFromMeeting } from "@platform/utils/getUserMeetingRoleFromMeeting";

export type Args = {
  isWithStandaloneMeetings?: boolean;
};

export const useUserCircle = (
  { isWithStandaloneMeetings = false }: Args = {
    isWithStandaloneMeetings: false,
  }
): Circle | undefined => {
  const { userCircles, user } = usePlatform();

  const userTimezoneOption = useUserTimezoneOption();

  const circle = userCircles?.[0];

  return useMemo(
    () =>
      circle
        ? {
            ...circle,
            users: circle?.users
              .map((participant) => ({
                ...participant,
                isUser: participant.id === user?.id,
              }))
              .sort((a) => (a.isUser ? -1 : 1)),
            meetings: (() => {
              const meetings = circle?.meetings.filter((meeting) =>
                isWithStandaloneMeetings
                  ? true
                  : meeting.meeting_type !== "meeting_standalone"
              );

              // Get the first meeting's time zone for comparison
              const firstMeetingTimeZone =
                meetings.length > 0
                  ? moment(meetings[0].start_time)
                      .tz(userTimezoneOption?.name ?? "UTC")
                      .format("h:mma")
                  : null;

              return circle?.meetings
                .filter((meeting) =>
                  isWithStandaloneMeetings
                    ? true
                    : meeting.meeting_type !== "meeting_standalone"
                )
                .map((meeting, index) => {
                  const localStartTime = moment(meeting.start_time)
                    .tz(userTimezoneOption?.name ?? "UTC")
                    .format("MMM Do h:mma z");

                  // Extract the time and timezone part (h:mma z)
                  const timeWithoutZone = moment(meeting.start_time)
                    .tz(userTimezoneOption?.name ?? "UTC")
                    .format("h:mma");

                  const isTimeShifted =
                    index > 0 && timeWithoutZone !== firstMeetingTimeZone;

                  return {
                    ...meeting,
                    localStartTime,
                    isTimeShifted,
                    userRole: getUserMeetingRoleFromMeeting(
                      meeting,
                      user?.id ?? null
                    ),
                  };
                });
            })(),
          }
        : undefined,
    [circle, userTimezoneOption]
  );
};

export default useUserCircle;
